import {
  AdjustFeatures,
  AdjustTerms,
  AloftPrice,
  StudiesPair,
  ToolkitProperty,
  ToolkitStudyV1Request,
  addressToMultilineAddress,
  adjustTermToFeature,
  dynamicScalarToString,
  isDynamicScalar,
} from '@aloft/toolkit-shared';

import { Td } from '../../../components/pdf-export/Td/Td.js';
import { TdProps } from '../../../components/pdf-export/Td/types.js';
import { Text } from '../../../components/pdf-export/Text/Text.js';
import { Tr } from '../../../components/pdf-export/Tr/Tr.js';
import { getTotalAdjustedValuePaired } from '../../adjustPairedSalesStandalone/getTotalAdjustedValuePaired.js';
import { isPairedSalesCustomAnalysisFeature } from '../../adjustPairedSalesStandalone/utils/isPairedSalesCustomAnalysisFeature.js';
import { makeDifferenceString } from '../utils/makeDifferenceString.js';
import { sharedTableStyles } from './sharedTableStyles.js';

const PLACEHOLDER = '-';

function getAddress(property: ToolkitProperty): string {
  const [firstLine] = addressToMultilineAddress(property.address);
  return firstLine;
}

function getGLA(property: ToolkitProperty): string {
  return property.grossLivingArea?.toSqFtValueStringRounded() ?? PLACEHOLDER;
}

function getSite(property: ToolkitProperty): string {
  return property.site?.toSqFtValueStringRounded() ?? PLACEHOLDER;
}

function getSaleDate(property: ToolkitProperty): string {
  return property.closeDate?.format('MM-dd-yyyy') ?? PLACEHOLDER;
}

function getP1AdjSalePrice(property: ToolkitProperty): string {
  const price = property.salePrice ?? AloftPrice.zero;
  return price.toString(true);
}

function getP2AdjSalePrice(
  property: ToolkitProperty,
  adjustments: StudiesPair['adjustments'],
): string {
  const price = property.salePrice ?? AloftPrice.zero;
  return getTotalAdjustedValuePaired(price, adjustments)?.toString(true) ?? PLACEHOLDER;
}

function getDynamicTermValue(
  property: ToolkitProperty,
  term: ToolkitStudyV1Request['analysisFeature'],
): string {
  if (isPairedSalesCustomAnalysisFeature(term)) {
    return property.custom ?? PLACEHOLDER;
  }

  const termInfo = AdjustTerms[term];
  if (termInfo.isManual || termInfo.featureKey === 'additionalFeature') return PLACEHOLDER;

  const featureKey = adjustTermToFeature(term);
  if (!featureKey) return PLACEHOLDER;
  if (featureKey === 'listToSale') return PLACEHOLDER;

  const featureInfo = AdjustFeatures[featureKey];
  const propertyKey = featureInfo.propertyKeys?.[0];
  if (!propertyKey) return PLACEHOLDER;

  const value = featureInfo.getAnalysisValue(property) ?? PLACEHOLDER;

  if (typeof value === 'string') return value;
  return (isDynamicScalar(value) ? dynamicScalarToString(value) : null) ?? '-';
}

type Props = {
  index: number;
  pair: StudiesPair;
  term: ToolkitStudyV1Request['analysisFeature'];
  reconciledValue: ToolkitStudyV1Request['body']['reconciledValue'];
};

export function StudyTableRow({ index, pair, term, reconciledValue }: Props) {
  const isFirstRow = index === 0;

  const sharedProps: TdProps = {
    isFirstRow,
    minHeight: '28',
    gap: '4',
    justifyContent: 'space-around',
  };

  return (
    <Tr wrap={false}>
      <Td isFirstColumn {...sharedProps} style={sharedTableStyles.address}>
        <Text variant="td">{getAddress(pair.p1)}</Text>
        <Text variant="td">{getAddress(pair.p2)}</Text>
      </Td>
      <Td {...sharedProps} style={sharedTableStyles.gla}>
        <Text variant="td">{getGLA(pair.p1)}</Text>
        <Text variant="td">{getGLA(pair.p2)}</Text>
      </Td>
      <Td {...sharedProps} style={sharedTableStyles.site}>
        <Text variant="td">{getSite(pair.p1)}</Text>
        <Text variant="td">{getSite(pair.p2)}</Text>
      </Td>
      <Td {...sharedProps} style={sharedTableStyles.saleDate}>
        <Text variant="td">{getSaleDate(pair.p1)}</Text>
        <Text variant="td">{getSaleDate(pair.p2)}</Text>
      </Td>
      <Td {...sharedProps} style={sharedTableStyles.salePrice}>
        <Text variant="td">{getP1AdjSalePrice(pair.p1)}</Text>
        <Text variant="td">{getP2AdjSalePrice(pair.p2, pair.adjustments)}</Text>
      </Td>
      <Td {...sharedProps} style={sharedTableStyles.term}>
        <Text variant="td">{getDynamicTermValue(pair.p1, term)}</Text>
        <Text variant="td">{getDynamicTermValue(pair.p2, term)}</Text>
      </Td>
      <Td {...sharedProps} style={sharedTableStyles.difference}>
        <Text variant="td">{makeDifferenceString(pair, reconciledValue, term)}</Text>
      </Td>
    </Tr>
  );
}
